import { GRAYS } from "@eldrun/content";
import { Color, DoubleSide, ShaderMaterial, SRGBColorSpace } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";

const vertexShader = /* glsl */ `
${assemble("vertex", ["atmo"])}
uniform float uTorcao;
uniform float uVelGiro;
uniform float uAltura;
out vec2 vUv;
out vec3 vWorldPos;

void main() {
  vUv = uv;
  // Torce o funil: o giro cresce com a altura e balança de leve no tempo.
  float h = position.y / uAltura;
  float a = h * uTorcao + uTime * uVelGiro;
  vec3 p = vec3(position.x * cos(a) - position.z * sin(a), position.y, position.x * sin(a) + position.z * cos(a));
  p.x += sin(uTime * 1.7 + h * 3.0) * 0.25 * h;
  p.z += cos(uTime * 1.3 + h * 2.0) * 0.25 * h;
  vec4 world = modelMatrix * vec4(p, 1.0);
  vWorldPos = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "noise", "fogGlow", "styleCore"])}
uniform float uVelGiro;
uniform vec3 uCorPoeira;
uniform vec3 uCorFolhas;
uniform float uOpacidade;
uniform float uCalmo;
in vec2 vUv;
in vec3 vWorldPos;

void main() {
  // Faixas de poeira que sobem girando; folhas como manchas pequenas e mais escuras.
  vec2 p = vec2(vUv.x * 6.0 + uTime * uVelGiro * 0.35, vUv.y * 3.0 - uTime * 1.4);
  float dust = smoothstep(0.35, 0.85, noise(p) * 0.7 + noise(p * 2.7) * 0.3);
  float leaf = step(0.82, noise(vec2(vUv.x * 18.0 + uTime * uVelGiro, vUv.y * 9.0 - uTime * 2.0)));
  vec3 color = mix(uCorPoeira, uCorFolhas, leaf);
  color *= mix(uAmbientSky * 0.6 + uLightColor * 0.5, vec3(1.0), 0.35);
  // Malha neon (F60): o Diabrete é neutro (travesso, não inimigo): poeira em cinza.
  color = styleGray(color);
  // Some nas pontas e quando o Saci se acalma (Peneira).
  float edges = smoothstep(0.0, 0.12, vUv.y) * (1.0 - smoothstep(0.75, 1.0, vUv.y));
  float alpha = (0.18 + 0.6 * dust + 0.5 * leaf) * edges * uOpacidade * (1.0 - uCalmo);
  gl_FragColor = vec4(applyFog(color, vWorldPos, cameraPosition), alpha);
  #include <colorspace_fragment>
}
`;

/** Redemoinho do Saci (doc 05 §8): funil torcido de poeira e folhas, num único material por redemoinho. */
export function createWhirlwindMaterial(atmosphere: AtmosphereUniforms, altura: number): ShaderMaterial {
  return new ShaderMaterial({
    name: "redemoinho",
    uniforms: {
      ...atmosphere,
      uTorcao: { value: 5.5 },
      uVelGiro: { value: 4 },
      uAltura: { value: altura },
      // Perigo neutro (doc 09 §1.3, F75): poeira e folhas em cinza; o giro é o padrão.
      uCorPoeira: { value: new Color().setRGB(GRAYS.prata, GRAYS.prata, GRAYS.prata, SRGBColorSpace) },
      uCorFolhas: { value: new Color().setRGB(GRAYS.escuro, GRAYS.escuro, GRAYS.escuro, SRGBColorSpace) },
      uOpacidade: { value: 1 },
      uCalmo: { value: 0 },
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
  });
}
